/** @jsx jsx */
import React, { Fragment, useRef, useState } from 'react'
import { jsx } from '@emotion/core'
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react'
import { Keyboard, Navigation, Pagination } from 'swiper'
import { Schema$SportIssue } from '@interface/sport-issue'
import { MATCH_INSIGHT_TYPE, SPORT_ISSUE_TYPE } from '@v2/constants/sport-issue'
import { isMobile } from 'react-device-detect'
import { YouTubeEmbed } from 'react-social-media-embed'
import { PopupSwiperGuideWrap, PopupSwiperWrap } from '@views/Matchup/MatchUpDetail/index.style'
import { ModalBg, TxtLimit2 } from '@styles/base.style'
import SwiperGuideTxt from '@assets/images/text/swiper-guide-txt.png'
import SwiperGuideIcon from '@assets/images/icon/etc_icon/swiper-guide-icon.png'
import { Button } from '@styles/button.style'
import { TimelineEventType } from '@interface/chat'
import { ScoreTimelineItem } from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/ScoreTimelineItem'
import { getMatchInsightByTimeline } from '@utils/utils'
import { HalftimeTimelineItem } from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/HalftimeTimelineItem'
import { LOCALSTORAGE_KEYS } from '@constants/index'
import StorageUtils from '@utils/storage-utils'
import LINK_OUT_ICON from '@assets/images/icon/link-out.png'
import PopupUtils from '@utils/popup-utils'
import useRootStore from '@store/useRootStore'
import { openSourceByApp } from '@utils/source-app-link'

interface ScoreData {
  homeTeamName: string
  awayTeamName: string
  homeTeamEmblemUrl: string
  awayTeamEmblemUrl: string
  sportId: number
}
interface SportIssueSwiperProps {
  data: Schema$SportIssue[]
  id: number
  scoreData: ScoreData
}

export const SportIssueSwiper: React.FC<SportIssueSwiperProps> = (props: SportIssueSwiperProps) => {
  const swiperList = props.data.filter((item) => item.sportIssueType === SPORT_ISSUE_TYPE.MATCH_INSIGHT)
  const initialIndex = swiperList.findIndex((item) => item.matchInsight.id === props.id)

  const [activeIndex, setActiveIndex] = useState(initialIndex)

  const { globalStore } = useRootStore()

  const onClickSource = (source: string): void => {
    if (!isMobile) {
      globalStore.toMoveWebHomePage(source, 'popup')
      return
    }

    openSourceByApp(source, () => PopupUtils.showSourceUrlPopup(source))
  }

  const [betSlipGuideClass, setBetSlipGuideClass] = useState('')
  const onClickBetSlipGuideClose = () => {
    setBetSlipGuideClass('adv-hidden')
  }

  const popupSwiperWrapRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (isMobile) return
    const wrap = popupSwiperWrapRef.current
    if (!wrap) return

    const prevButton = wrap.querySelector('.swiper-button-prev') as HTMLElement | null
    const nextButton = wrap.querySelector('.swiper-button-next') as HTMLElement | null

    if (!prevButton || !nextButton) return

    const isFirst = activeIndex === 0
    const isLast = activeIndex === swiperList.length - 1
    const isSingle = swiperList.length === 1

    prevButton.classList.remove('swiper-button-disabled')
    nextButton.classList.remove('swiper-button-disabled')


    if (isSingle) {
      prevButton.classList.add('swiper-button-disabled')
      nextButton.classList.add('swiper-button-disabled')
      return
    }
    if (isFirst) {
      prevButton.classList.add('swiper-button-disabled')
    } else if (isLast) {
      nextButton.classList.add('swiper-button-disabled')
    }
  }, [activeIndex, isMobile])

  React.useEffect(() => {
    if (!isMobile) return

    const hasSeenGuide = StorageUtils.getBoolValue(LOCALSTORAGE_KEYS.BET_SLIP_GUIDE_SEEN)
    if (!hasSeenGuide) {
      setBetSlipGuideClass('')
      StorageUtils.saveToLocal(LOCALSTORAGE_KEYS.BET_SLIP_GUIDE_SEEN, true)
    } else {
      setBetSlipGuideClass('adv-hidden')
    }
  }, [isMobile])

  const playerRef = useRef([])
  const initPlayer = (e) => {
    playerRef.current.push(e.target)
  }

  return (
    <Fragment>
      <PopupSwiperWrap ref={popupSwiperWrapRef} className={isMobile ? '' : 'pc'}>
        <Swiper
          navigation={!isMobile}
          pagination={{
            clickable: true,
            dynamicBullets: true,
            dynamicMainBullets: swiperList.length < 5 ? swiperList.length : 5,
          }}
          keyboard={{
            enabled: true,
          }}
          modules={[Navigation, Pagination, Keyboard]}
          slidesPerView={1}
          loop={swiperList.length > 1 && !isMobile}
          initialSlide={initialIndex}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.realIndex)
            playerRef.current.forEach((player) => {
              if (player.pauseVideo) {
                player.pauseVideo()
              }
            })
          }}
        >
          {swiperList.map((item, index) => {
            const { matchInsightType, attachment } = item.matchInsight
            if (matchInsightType === MATCH_INSIGHT_TYPE.IMAGE) {
              const source = item.matchInsight.sourceUrl
              return (
                <SwiperSlide key={item.matchInsight.id}>
                  <div className="swiper-image-wrap">
                    <img src={attachment} alt={item.content}/>
                  </div>
                  {source ? (
                    <button className="swiper-source-button" onClick={() => onClickSource(source)}>
                      <p className="swiper-title">{item.content}</p>
                      <span className="swiper-source">
                        <img src={LINK_OUT_ICON} alt="원문보기"/>
                        원문보기
                      </span>
                    </button>
                  ) : (
                    <p className="swiper-title">{item.content}</p>
                  )}
                </SwiperSlide>
              )
            } else if (matchInsightType === MATCH_INSIGHT_TYPE.URL) {
              return (
                <SwiperSlide key={item.matchInsight.id}>
                  <div className="swiper-youtube-wrap">
                    <YouTubeEmbed url={attachment} width="100%" youTubeProps={{onReady:initPlayer}}/>
                  </div>
                  <p className="swiper-title">{item.content}</p>
                </SwiperSlide>
              )
            } else if (
              matchInsightType === MATCH_INSIGHT_TYPE.TIMELINE && props.scoreData
            ) {
              const {
                eventType, scoreTeamName, scoreTime, goalScorer, emblemUrl, homeTeamScore, awayTeamScore,
              } = getMatchInsightByTimeline({
                timeline: item.matchInsight.timeline.content,
                homeTeamName: props.scoreData.homeTeamName,
                awayTeamName: props.scoreData.awayTeamName,
                homeTeamEmblemUrl: props.scoreData.homeTeamEmblemUrl,
                awayTeamEmblemUrl: props.scoreData.awayTeamEmblemUrl,
              })
              if (eventType === TimelineEventType.SCORE_CHANGE) {
                return (
                  <SwiperSlide key={item.matchInsight.id}>
                    <ScoreTimelineItem
                      className="full-popup"
                      scoreTimelineData={{
                        teamName: scoreTeamName,
                        time: scoreTime,
                        playerName: goalScorer,
                        teamEmblemUrl: emblemUrl,
                      }}
                    />
                    <p className="txt" css={TxtLimit2}>{props.scoreData.homeTeamName}{homeTeamScore}:{awayTeamScore}{props.scoreData.awayTeamName}</p>
                  </SwiperSlide>
                )
              } else if (eventType === TimelineEventType.PERIOD_SCORE) {
                return (
                  <SwiperSlide key={item.matchInsight.id}>
                    <HalftimeTimelineItem className="full-popup" scoreTimelineData={{
                      homeTeamEmblemUrl: props.scoreData.homeTeamEmblemUrl,
                      awayTeamEmblemUrl: props.scoreData.awayTeamEmblemUrl,
                      homeTeamName: props.scoreData.homeTeamName,
                      awayTeamName: props.scoreData.awayTeamName,
                      homeTeamScore: homeTeamScore,
                      awayTeamScore: awayTeamScore,
                      sportId: props.scoreData.sportId,
                    }}/>
                    <p className="txt" css={TxtLimit2}>{props.scoreData.homeTeamName}{homeTeamScore}:{awayTeamScore}{props.scoreData.awayTeamName}</p>
                  </SwiperSlide>
                )
              }
            }
          })}
        </Swiper>
      </PopupSwiperWrap>
      {isMobile && (
        <PopupSwiperGuideWrap className={betSlipGuideClass}>
          <ModalBg className="modal-bg"/>
          <Button className="guide-button" onClick={onClickBetSlipGuideClose}>
            <div className="guide-section">
              <img src={SwiperGuideIcon} alt="" className="icon-guide"/>
              <img src={SwiperGuideTxt} alt="좌우로 스와이프하여 확인할 수 있어요." className="txt-guide"/>
            </div>
          </Button>
        </PopupSwiperGuideWrap>
      )}
    </Fragment>
  )
}
